/**
 * La mesa de edición: el tablero de notas (M-14, M-15 · 18-sep-2026).
 *
 * El centro de la pantalla deja de ser la etapa y pasa a ser lo que te han dicho del libro. Tres
 * estados, en el orden en que se trabaja: lo que falta por decidir, lo que va en tu próxima pasada
 * y lo que te espera en Word. El orden dentro de «por decidir» es por lo que más mueve la nota
 * —gravedad y capítulo—, pero no obliga a nada: el autor decide, la casa recomienda.
 *
 * La ficha se abre al pulsar una nota y tiene tres decisiones y un sitio para tu indicación. Con J y
 * K se pasa de una a otra sin tocar el ratón: decidir veinte seguidas tiene que ser cómodo, porque
 * es lo que de verdad hace avanzar un libro.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { PuertaPasada, anadirNota, catalogoModelos, costeDeLaPasada, decidirNota, deVueltaDeWord, dolares, ejecutarPasada, manifiestoDeVersion, modelosAsignados, notasAWord, notasDelLibro } from "../api";
import type { CambioDeVersion, EstadoNota, ModeloCatalogo, Nota } from "../api";
import { estimarCoste, frasesDeVuelta, lugarDeNota } from "../logica";

const COLUMNAS: { estado: EstadoNota; titulo: string; pie: string }[] = [
  { estado: "por_decidir", titulo: "Por decidir", pie: "Ordenadas por lo que más mueve la nota" },
  { estado: "en_pasada", titulo: "Tu próxima pasada", pie: "Lo que has dicho que se hace" },
  { estado: "en_word", titulo: "En Word, te esperan", pie: "Escrito en una versión, sin decidir por ti" },
];

const GRAVE: Record<string, string> = { alta: "Importante", media: "Medio", baja: "Menor" };

export default function Tablero({ carpeta, doc, pulso = 0, alDecidir, alLanzar }: {
  carpeta: string; doc: string | null; pulso?: number; alDecidir?: () => void; alLanzar?: () => void;
}) {
  const [notas, setNotas] = useState<Nota[] | null>(null);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [ocupada, setOcupada] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [nueva, setNueva] = useState(false);
  const [aWord, setAWord] = useState<string | null>(null);   // M-27: notas dentro del documento
  const [puerta, setPuerta] = useState<string | null>(null); // lo que impide lanzar la pasada
  const [vuelta, setVuelta] = useState<string | null>(null);  // M-21: lo leído del documento
  // M-16: los filtros son LENTES, no candados: ninguno impide trabajar, solo enfocan el tablero.
  const [capitulo, setCapitulo] = useState<number | null>(null);
  const [nivel, setNivel] = useState<Nota["nivel"] | null>(null);
  const [gravedad, setGravedad] = useState<Nota["gravedad"] | null>(null);
  const [fuente, setFuente] = useState<string | null>(null);
  const [descartadas, setDescartadas] = useState<"ocultas" | "visibles" | "solo">("ocultas");
  const caja = useRef<HTMLDivElement>(null);

  const cargar = useCallback(() => {
    if (!carpeta) return;
    notasDelLibro(carpeta, doc).then((d) => setNotas(d.notas)).catch(() => setNotas([]));
  }, [carpeta, doc]);
  useEffect(cargar, [cargar, pulso]);

  const pasaFiltros = (n: Nota) =>
    (capitulo == null || n.ancla?.capitulo === capitulo || (n.capitulos ?? []).includes(capitulo))
    && (nivel == null || n.nivel === nivel)
    && (gravedad == null || n.gravedad === gravedad)
    && (fuente == null || n.fuentes.some((f) => f.informe === fuente));
  const visibles = (notas ?? []).filter(pasaFiltros);
  const esDescartada = (n: Nota) => n.estado === "no_aplica" || n.estado === "luego";
  const porEstado = (e: EstadoNota) => visibles.filter((n) => n.estado === e && descartadas !== "solo");
  // Los capítulos con notas abiertas, para la columna de la izquierda.
  const deCapitulos = (n: Nota) => [...(n.ancla?.capitulo != null ? [n.ancla.capitulo] : []), ...(n.capitulos ?? [])];
  const capitulos = [...new Set((notas ?? []).flatMap(deCapitulos))].sort((a, b) => a - b);
  const abiertasDe = (c: number) => (notas ?? []).filter((n) => deCapitulos(n).includes(c) && !esDescartada(n) && n.estado !== "resuelta").length;
  const fuentes = [...new Set((notas ?? []).flatMap((n) => n.fuentes.map((f) => f.informe)))];
  const fueraDeJuego = visibles.filter(esDescartada);
  const pendientes = porEstado("por_decidir");
  const nota = (notas ?? []).find((n) => n.id === abierta) ?? null;

  async function decidir(n: Nota, estado: EstadoNota, comentario?: string) {
    setOcupada(n.id); setFallo(null);
    try {
      const puesta = await decidirNota(carpeta, doc, n.id, { estado, ...(comentario !== undefined ? { comentario } : {}) });
      setNotas((v) => (v ?? []).map((x) => (x.id === puesta.id ? puesta : x)));
      // Al decidir, se pasa a la siguiente por decidir: así se encadena sin volver al tablero.
      const siguiente = pendientes.find((x) => x.id !== n.id);
      setAbierta(estado === "por_decidir" ? n.id : siguiente?.id ?? null);
      alDecidir?.();
    } catch (e) { setFallo((e as Error).message); } finally { setOcupada(null); }
  }

  // J y K, como en la ficha de Galleys: pasar de nota sin soltar el teclado.
  useEffect(() => {
    const teclas = (e: KeyboardEvent) => {
      if (!nota || e.metaKey || e.ctrlKey || e.altKey) return;
      const dentro = (e.target as HTMLElement)?.closest("input, textarea, select");
      if (dentro) return;
      const lista = (notas ?? []).filter((x) => x.estado === nota.estado);
      const i = lista.findIndex((x) => x.id === nota.id);
      if (e.key === "j" || e.key === "J") { e.preventDefault(); setAbierta(lista[Math.min(i + 1, lista.length - 1)]?.id ?? nota.id); }
      if (e.key === "k" || e.key === "K") { e.preventDefault(); setAbierta(lista[Math.max(i - 1, 0)]?.id ?? nota.id); }
      if (e.key === "Escape") setAbierta(null);
    };
    window.addEventListener("keydown", teclas);
    return () => window.removeEventListener("keydown", teclas);
  }, [nota, notas]);

  useEffect(() => { if (abierta) caja.current?.scrollIntoView({ block: "nearest" }); }, [abierta]);

  if (notas === null) return <section className="carta"><h2>Notas</h2><p className="vacio">Leyendo lo que te han dicho del libro…</p></section>;

  if (!notas.length) {
    return (
      <section className="carta">
        <h2>Notas<span className="sub">lo que te han dicho del libro</span></h2>
        <p className="vacio">
          Todavía no hay ninguna. Cuando pidas el informe, cada cosa que el lector señale con una cita
          que se pueda localizar en tu documento aparecerá aquí, y tú decides qué hacer con ella.
          {doc ? "" : " Elige antes tu manuscrito."}
        </p>
        {doc && <div className="botones"><button className="btn fantasma" onClick={() => setNueva(true)}>Escribir una nota mía</button></div>}
        {nueva && <NotaNueva carpeta={carpeta} doc={doc} alHecho={() => { setNueva(false); cargar(); }} alCerrar={() => setNueva(false)} />}
      </section>
    );
  }

  return (
    <>
      <section className="carta">
        <h2>Notas<span className="sub">{pendientes.length} por decidir · {notas.length} en total</span></h2>
        {/* M-16: filtrar es enfocar. El contador de cada capítulo cuenta lo que sigue abierto. */}
        <div className="adjuntos filtros-notas">
          <button className={"chip" + (capitulo == null && nivel == null && gravedad == null && fuente == null ? " puesto" : "")}
                  onClick={() => { setCapitulo(null); setNivel(null); setGravedad(null); setFuente(null); }}>Todo el libro</button>
          {capitulos.map((c) => (
            <button key={c} className={"chip" + (capitulo === c ? " puesto" : "")} onClick={() => setCapitulo(capitulo === c ? null : c)}>
              Capítulo {c} · {abiertasDe(c)}
            </button>
          ))}
          {(["estructura", "capitulo", "linea"] as const).map((x) => (
            <button key={x} className={"chip" + (nivel === x ? " puesto" : "")} onClick={() => setNivel(nivel === x ? null : x)}>
              {x === "linea" ? "Línea" : x === "estructura" ? "Estructura" : "Capítulo"}
            </button>
          ))}
          {(["alta", "media", "baja"] as const).map((g) => (
            <button key={g} className={"chip" + (gravedad === g ? " puesto" : "")} onClick={() => setGravedad(gravedad === g ? null : g)}>{GRAVE[g]}</button>
          ))}
          {fuentes.length > 1 && fuentes.map((f) => (
            <button key={f} className={"chip" + (fuente === f ? " puesto" : "")} onClick={() => setFuente(fuente === f ? null : f)}>{f}</button>
          ))}
          <button className={"chip" + (descartadas !== "ocultas" ? " puesto" : "")}
                  onClick={() => setDescartadas(descartadas === "ocultas" ? "visibles" : descartadas === "visibles" ? "solo" : "ocultas")}>
            Descartadas: {descartadas === "ocultas" ? "ocultas" : descartadas === "visibles" ? "visibles" : "solo ellas"}
          </button>
        </div>
        <div className="tablero">
          {COLUMNAS.map((c) => {
            const suyas = porEstado(c.estado);
            return (
              <div className="columna" key={c.estado}>
                <div className="cab"><b>{c.titulo}</b><span className="chip-estado">{suyas.length}</span></div>
                <p className="vacio">{c.pie}</p>
                {suyas.map((n) => (
                  <button key={n.id} className={"nota-tarjeta" + (abierta === n.id ? " abierta" : "")} onClick={() => setAbierta(n.id === abierta ? null : n.id)}>
                    <span className="cabecera">
                      <span className={"grado " + n.gravedad}>{GRAVE[n.gravedad]}</span>
                      <span className="vacio">{n.origen === "pasada" ? ["Del plan", n.dimension !== "plan" ? n.dimension : null].filter(Boolean).join(" · ")
                        : [n.nivel === "linea" ? "Línea" : n.nivel === "estructura" ? "Estructura" : "Capítulo", n.dimension].filter(Boolean).join(" · ")}</span>
                      <span className="vacio mono">{n.id}</span>
                    </span>
                    <b>{n.titulo}</b>
                    {n.ancla?.cita && <i className="cita">«{n.ancla.cita.slice(0, 90)}{n.ancla.cita.length > 90 ? "…" : ""}»</i>}
                    <span className="vacio">
                      {lugarDeNota(n)}
                      {n.fuentes.length > 1 ? ` · lo dicen ${n.fuentes.length}` : ""}
                    </span>
                    {/* R0-6: vuelve a ti porque rechazaste su cambio; se dice, no se esconde. */}
                    {n.estado === "por_decidir" && n.devuelta && <span className="vacio">Rechazaste su cambio en Word: decide qué hacer con ella.</span>}
                  </button>
                ))}
                {!suyas.length && <p className="vacio">{c.estado === "por_decidir" ? "Nada pendiente." : c.estado === "en_pasada" ? "Lo que decidas «a la pasada» aparece aquí." : "Nada escrito todavía."}</p>}
                {/* M-18: la pasada hace lo que TÚ has puesto en ella, en una versión nueva con los
                    cambios marcados. No hace falta plan. */}
                {/* M-21: «resuelto» no se declara, se comprueba. Se lee el documento que guardaste. */}
                {c.estado === "en_word" && suyas.length > 0 && (
                  <button className="btn" disabled={!doc || ocupada === "vuelta"} onClick={async () => {
                    if (!doc) return;
                    setOcupada("vuelta"); setFallo(null); setVuelta(null);
                    try {
                      const r = await deVueltaDeWord(carpeta, doc);
                      setVuelta(frasesDeVuelta(r));
                      cargar(); alDecidir?.();
                    } catch (e) { setFallo((e as Error).message); } finally { setOcupada(null); }
                  }}>
                    {ocupada === "vuelta" ? "Leyendo tu documento…" : "Ya lo he decidido en Word"}
                  </button>
                )}
                {c.estado === "en_pasada" && suyas.length > 0 && (
                  <CosteDeLaPasada carpeta={carpeta} doc={doc} notas={suyas.map((n) => n.id)} />
                )}
                {c.estado === "en_pasada" && suyas.length > 0 && (
                  <button className="btn" disabled={!doc || ocupada === "pasada"} onClick={async () => {
                    if (!doc) return;
                    setOcupada("pasada"); setFallo(null); setPuerta(null);
                    try {
                      await ejecutarPasada(carpeta, doc, null, suyas.map((n) => n.id));
                      alLanzar?.();
                      cargar();
                    } catch (e) {
                      if (e instanceof PuertaPasada) setPuerta(e.message); else setFallo((e as Error).message);
                    } finally { setOcupada(null); }
                  }}>
                    {ocupada === "pasada" ? "Escribiendo la versión…" : `Ejecutar la pasada con ${suyas.length === 1 ? "esta nota" : `estas ${suyas.length} notas`}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="botones">
          <button className="btn fantasma" onClick={() => setNueva(true)}>Escribir una nota mía</button>
          {/* M-27: en Word, «Ir a → Marcador» lista las notas y salta a su párrafo. Sin complemento. */}
          <button className="btn fantasma" disabled={!doc || ocupada === "word"} onClick={async () => {
            if (!doc) return;
            setOcupada("word"); setFallo(null); setAWord(null);
            try {
              const r = await notasAWord(carpeta, doc, true);
              setAWord(`${r.puestos.length} notas en ${r.documento.split(/[/\\]/).pop()}${r.fallidos.length ? ` · ${r.fallidos.length} sin sitio` : ""}`);
            } catch (e) { setFallo((e as Error).message); } finally { setOcupada(null); }
          }}>{ocupada === "word" ? "Escribiendo…" : "Llevar las notas a Word"}</button>
          <span className="vacio">Con una nota abierta, J y K pasan a la siguiente y a la anterior.</span>
        </div>
        {aWord && (
          <p className="vacio">
            {aWord}. Es una copia: tu manuscrito no se toca. En Word, «Ir a → Marcador» te lleva a cada
            nota, y los comentarios salen en el margen.
          </p>
        )}
        {nueva && <NotaNueva carpeta={carpeta} doc={doc} alHecho={() => { setNueva(false); cargar(); }} alCerrar={() => setNueva(false)} />}
        {/* Lo que decidiste no hacer también es tuyo: se puede mirar y se puede recuperar. */}
        {descartadas !== "ocultas" && (
          <div className="columna" style={{ marginTop: 12 }}>
            <div className="cab"><b>Descartadas y para luego</b><span className="chip-estado">{fueraDeJuego.length}</span></div>
            {fueraDeJuego.map((n) => (
              <button key={n.id} className="nota-tarjeta" onClick={() => setAbierta(n.id === abierta ? null : n.id)}>
                <span className="cabecera">
                  <span className="chip-estado">{n.estado === "no_aplica" ? "no aplica" : "luego"}</span>
                  <span className="vacio mono">{n.id}</span>
                </span>
                <b>{n.titulo}</b>
                {n.comentario && <span className="vacio">Dijiste: {n.comentario}</span>}
              </button>
            ))}
            {!fueraDeJuego.length && <p className="vacio">No has descartado ninguna.</p>}
          </div>
        )}
        {vuelta && <p className="vacio">{vuelta} Aquí no se acepta ni se rechaza nada: se lee lo que hiciste tú.</p>}
        {puerta && <div className="aviso">{puerta}</div>}
        {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
      </section>

      {nota && (
        <section className="carta" ref={caja}>
          <Ficha n={nota} carpeta={carpeta} doc={doc} ocupada={ocupada === nota.id} alDecidir={decidir} alCerrar={() => setAbierta(null)} />
        </section>
      )}
    </>
  );
}

/** La ficha: lo que dice la nota, dónde está y las tres decisiones. */
function Ficha({ n, carpeta, doc, ocupada, alDecidir, alCerrar }: {
  n: Nota; carpeta: string; doc: string | null; ocupada: boolean;
  alDecidir: (n: Nota, estado: EstadoNota, comentario?: string) => void;
  alCerrar: () => void;
}) {
  const [comentario, setComentario] = useState(n.comentario ?? "");
  useEffect(() => { setComentario(n.comentario ?? ""); }, [n.id]);
  return (
    <>
      <h2>
        <span className={"grado " + n.gravedad}>{GRAVE[n.gravedad]}</span> {n.titulo}
        <span className="sub">{n.id} · {lugarDeNota(n)}
          {n.ancla?.parrafo != null && n.origen !== "pasada" ? ` · párrafo ${n.ancla.parrafo}` : ""}</span>
      </h2>
      <p>{n.problema}</p>
      {n.ancla?.cita && <><p className="vacio">En tu manuscrito</p><blockquote className="cita-larga">«{n.ancla.cita}»</blockquote></>}
      {n.arreglo && <><p className="vacio">Arreglo propuesto</p><p>{n.arreglo}</p></>}
      {/* R0-6: lo que leyó el motor de tu documento, con sus palabras. */}
      {n.estado === "por_decidir" && n.devuelta && <p className="vacio">Leído de {n.devuelta.build ?? "tu documento"}: {n.devuelta.motivo}.</p>}
      {n.cierre && ["resuelta", "no_aplica", "por_comprobar"].includes(n.estado) && <p className="vacio">Leído de {n.cierre.build ?? "tu documento"}: {n.cierre.motivo}.</p>}
      <CambiosDeLaNota n={n} carpeta={carpeta} doc={doc} />
      {n.origen === "pasada"
        ? <p className="vacio">{n.fase != null
            ? `La escribió la fase ${n.fase} del plan, con varias pasadas en una sola versión: son los cambios que no contestaban a ninguna de tus notas. En Word, cada pasada firma con su nombre.`
            : `La escribió la pasada ${n.pasada} del plan con los cambios que no contestaban a ninguna de tus notas.`}</p>
        : <p className="vacio">También lo dijeron: {n.fuentes.map((f) => `${f.informe}${f.version ? ` · v${f.version}` : ""}`).join(" · ")}</p>}

      {n.origen === "pasada" ? (
        <>
          <p className="vacio">Se decide en Word: sus marcas llevan el nombre de la pasada, así que puedes aceptarlas o rechazarlas todas juntas. Guarda, y cuando el Studio lea tu documento se cerrará sola con lo que hayas hecho.</p>
          <div className="botones">
            {!["no_aplica", "resuelta"].includes(n.estado) && (
              <button className="btn fantasma" disabled={ocupada} onClick={() => alDecidir(n, "no_aplica")}>Quitarla del tablero</button>
            )}
            <button className="btn texto" onClick={alCerrar}>Cerrar</button>
          </div>
        </>
      ) : <>
      <label className="campo">
        <span>Tu indicación para la pasada <i>(opcional)</i></span>
        <div className="dir">
          <textarea className="escribir" rows={2} style={{ resize: "vertical" }} value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    placeholder="Por ejemplo: en los capítulos 15 a 25 prefiero cortar la tabla antes que rellenarla" />
        </div>
      </label>
      <p className="vacio">Lo que decidas aquí es lo que cita tu certificado. Descartar también es una decisión de autor.</p>

      <div className="botones">
        <button className="btn" disabled={ocupada} onClick={() => alDecidir(n, "en_pasada", comentario)}>A la pasada</button>
        <button className="btn fantasma" disabled={ocupada} onClick={() => alDecidir(n, "luego", comentario)}>Luego</button>
        <button className="btn fantasma" disabled={ocupada} onClick={() => alDecidir(n, "no_aplica", comentario)}>No aplica</button>
        {n.estado !== "por_decidir" && (
          <button className="btn texto" disabled={ocupada} onClick={() => alDecidir(n, "por_decidir", comentario)}>Volver a por decidir</button>
        )}
        <button className="btn texto" onClick={alCerrar}>Cerrar</button>
      </div>
      </>}
    </>
  );
}

/**
 * Lo que va a costar la pasada, antes de pulsar (M-20).
 *
 * Las palabras las cuenta el motor sobre los capítulos que de verdad van a viajar, y el precio sale
 * del catálogo real de OpenRouter: sin precio conocido, la línea se calla en vez de inventar una
 * cifra. El aviso de estructura sin decidir se dice y no impide nada: es la regla de la casa —se
 * recomienda, no se bloquea—.
 */
function CosteDeLaPasada({ carpeta, doc, notas }: { carpeta: string; doc: string | null; notas: string[] }) {
  const [d, setD] = useState<{ palabras: number; llamadas: number; aviso: string | null } | null>(null);
  const [catalogo, setCatalogo] = useState<ModeloCatalogo[]>([]);
  const [modelo, setModelo] = useState<string | null>(null);   // el del editor: es quien hace la pasada
  const clave = notas.join(",");
  useEffect(() => {
    setD(null);
    if (!doc || !carpeta || !notas.length) return;
    costeDeLaPasada(carpeta, doc, notas).then(setD).catch(() => setD(null));
    catalogoModelos().then((r) => setCatalogo(r.modelos)).catch(() => {});
    modelosAsignados().then((m) => setModelo(m.porAgente.editor ?? m.defecto)).catch(() => {});
  }, [carpeta, doc, clave]);
  if (!d) return null;
  // La pasada devuelve el capítulo entero reescrito: la salida pesa como la entrada.
  const e = estimarCoste(d.palabras, catalogo.find((m) => m.id === modelo), undefined, "reescritura");
  return (
    <>
      <p className="vacio">
        {d.palabras.toLocaleString("es-ES")} palabras en {d.llamadas} {d.llamadas === 1 ? "llamada" : "llamadas"}
        {e.coste != null ? ` · ~${dolares(e.coste)} de tu llave` : ""}
        {e.coste != null ? ` y ~${Math.max(1, Math.round(e.minutos))} min` : ""}.
      </p>
      {d.aviso && <p className="vacio">{d.aviso}</p>}
    </>
  );
}

/**
 * El porqué de cada cambio, antes de abrir Word (M-28).
 *
 * Es una lista de INTERVENCIONES —lo que se quita y lo que se pone—, no el manuscrito: el Studio no
 * tiene lector de libros y no lo va a tener. Se lee del manifiesto que escribió la pasada, así que
 * dice lo que de verdad se escribió, no lo que se pensaba escribir. Tras leer el documento de
 * vuelta, cada cambio lleva además lo que hiciste con él.
 */
function CambiosDeLaNota({ n, carpeta, doc }: { n: Nota; carpeta: string; doc: string | null }) {
  const [cambios, setCambios] = useState<CambioDeVersion[] | null | "sin">(null);
  const [abierto, setAbierto] = useState(false);
  useEffect(() => {
    setCambios(null); setAbierto(false);
    if (!n.build || !carpeta) return;
    manifiestoDeVersion(carpeta, doc, n.build)
      .then((m) => setCambios(m ? m.cambios.filter((c) => c.nota === n.id || (c.notas ?? []).includes(n.id)) : "sin"))
      .catch(() => setCambios("sin"));
  }, [n.id, n.build, carpeta, doc]);

  if (!n.build) return null;
  const RESULTADO: Record<string, string> = {
    aceptado: "Lo aceptaste", retocado: "Lo retocaste", rechazado: "Lo rechazaste",
    suyo: "Pusiste texto tuyo", sin_efecto: "No cambiaba nada",
    no_evaluable: "Demasiado corto para rastrearlo",
  };
  return (
    <div className="notas">
      <button className="btn texto" onClick={() => setAbierto(!abierto)}>
        {abierto ? "Ocultar" : "Ver"} {n.origen === "pasada" ? "los cambios de la pasada" : "lo que se cambió por esta nota"} en {n.build}
      </button>
      {abierto && cambios === null && <p className="vacio">Leyendo el manifiesto de la versión…</p>}
      {abierto && cambios === "sin" && (
        <p className="vacio">Esa versión se escribió antes de que se guardara el detalle de cada cambio,
          así que aquí no puedo decirte cuál fue. Lo verás marcado en Word.</p>
      )}
      {abierto && Array.isArray(cambios) && cambios.length === 0 && (
        <p className="vacio">La versión no lleva ningún cambio atribuido a esta nota.</p>
      )}
      {abierto && Array.isArray(cambios) && cambios.map((c) => (
        <div className="issue" key={c.n}>
          {c.quita && <del>{c.quita}</del>}
          {c.pone && <ins>{c.pone}</ins>}
          <p className="vacio">
            {c.por_que || "Sin motivo anotado"}
            {c.resultado ? ` · ${RESULTADO[c.resultado] ?? c.resultado}` : ""}
          </p>
        </div>
      ))}
    </div>
  );
}

/** Una nota del autor: lo que él ve y nadie le ha dicho. */
function NotaNueva({ carpeta, doc, alHecho, alCerrar }: { carpeta: string; doc: string | null; alHecho: () => void; alCerrar: () => void }) {
  const [titulo, setTitulo] = useState("");
  const [capitulo, setCapitulo] = useState("");
  const [cita, setCita] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  return (
    <div className="formulario" style={{ maxWidth: "none" }}>
      <label className="campo"><span>Qué has visto</span>
        <div className="dir"><input className="escribir" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Reordenar los apéndices" /></div></label>
      <label className="campo"><span>Capítulo <i>(opcional)</i></span>
        <div className="dir"><input className="escribir" value={capitulo} onChange={(e) => setCapitulo(e.target.value)} placeholder="31" /></div></label>
      <label className="campo"><span>Cita <i>(opcional: si la pones, se ancla al pasaje)</i></span>
        <div className="dir"><input className="escribir" value={cita} onChange={(e) => setCita(e.target.value)} /></div></label>
      <div className="botones">
        <button className="btn" disabled={!titulo.trim() || ocupado} onClick={async () => {
          setOcupado(true); setFallo(null);
          try {
            await anadirNota(carpeta, doc, { titulo: titulo.trim(), capitulo: capitulo ? Number(capitulo) : null, cita: cita.trim() });
            alHecho();
          } catch (e) { setFallo((e as Error).message); } finally { setOcupado(false); }
        }}>Añadirla a la pasada</button>
        <button className="btn texto" onClick={alCerrar}>Cancelar</button>
      </div>
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
    </div>
  );
}
